import React, { useEffect, useState, useCallback } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { waybillAPI, uomAPI } from '../../api/waybillAPI';
import WaybillLineTable from '../../components/billing/WaybillLineTable';
import CustomerSearch from '../../components/CustomerSearch';
import api from '../../api/axiosConfig';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import AuditTimeline from '../../components/billing/AuditTimeline';
import useGuardedMutation from '../../hooks/useGuardedMutation';

import systemSettingsAPI from '../../api/systemSettingsAPI';

function WaybillGLBadge({ status, error, waybillId, onRetry, loading }) {
  if (status === 'posted') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">GL Posted</span>;
  }
  if (status === 'failed') {
    return (
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700" title={error || 'GL posting failed'}>
          GL Failed
        </span>
        <button
          onClick={() => onRetry(waybillId)}
          disabled={loading}
          className="text-xs text-teal-600 hover:underline whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? 'Posting…' : 'Retry'}
        </button>
      </div>
    );
  }
  // pending — first-time post not yet attempted
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
        GL Pending
      </span>
      <button
        onClick={() => onRetry(waybillId)}
        disabled={loading}
        className="text-xs text-teal-600 hover:underline whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? 'Posting…' : 'Post to GL'}
      </button>
    </div>
  );
}

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const today = () => new Date().toISOString().split('T')[0];

const emptyForm = {
  SerialNo: '',
  TransactionDate: today(),
  Shipper: '',
  ShipperAddress: '',
  CustomerId: '',
  CustomerAddress: '',
  Origin: '',
  Destination: '',
  Vessel: '',
  Voyage: '',
  PlateNo: '',
  VanNo: '',
  SealNo: '',
  VanSize: '',
  RateCode: '',
  HasWithHolding: false,
  IsDelivered: false,
  Description: '',
  referenceNumber: '',
  notes: '',
  customerName: '',
  ShipmentType: 'freightforwarding',
  pullOutDate: '',
};

export default function WaybillEditPage() {
  const { id } = useParams();
  const isAdd = !id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [uomOptions, setUomOptions] = useState([]);
  const [loading, setLoading] = useState(!isAdd);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isVoided, setIsVoided] = useState(false);
  const [voidedInfo, setVoidedInfo] = useState({ reason: '', at: null });
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFetched, setAuditFetched] = useState(false);
  const [linkedBillings, setLinkedBillings] = useState([]);
  const [linkedBillingsLoaded, setLinkedBillingsLoaded] = useState(false);
  const [billingsMenuOpen, setBillingsMenuOpen] = useState(false);
  const [minAmount, setMinAmount] = useState(250);
  const [glPostingStatus, setGlPostingStatus] = useState('pending');
  const [glPostingError, setGlPostingError] = useState(null);
  const [glPosting, setGlPosting] = useState(false);
  useEffect(() => {
    if (isAdd) {
      systemSettingsAPI.get()
        .then(r => setMinAmount(r.data?.data?.minimumBillingAmount ?? 250))
        .catch(() => {});
    }
  }, [isAdd]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (isAdd || !id) {
      setLinkedBillingsLoaded(true);
      return;
    }
    waybillAPI.getLinkedBillings(id)
      .then((r) => {
        setLinkedBillings(Array.isArray(r.data?.data) ? r.data.data : []);
      })
      .catch(() => {
        setLinkedBillings([]);
      })
      .finally(() => setLinkedBillingsLoaded(true));
  }, [id, isAdd]);

  useEffect(() => {
    if (!billingsMenuOpen) return;
    const handler = (e) => {
      if (!e.target.closest('[data-billings-menu]')) {
        setBillingsMenuOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [billingsMenuOpen]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'audit' && !auditFetched && id) {
      setAuditLoading(true);
      waybillAPI.getAuditLog(id)
        .then((res) => {
          setAuditLog(Array.isArray(res.data?.data) ? res.data.data : []);
          setAuditFetched(true);
        })
        .catch(() => {})
        .finally(() => setAuditLoading(false));
    }
  };

  const handleRetryGL = async (waybillId) => {
    if (glPosting) return;
    setGlPosting(true);
    try {
      await waybillAPI.retryWaybillGLPost(waybillId);
      setGlPostingStatus('posted');
      setGlPostingError(null);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'GL retry failed';
      setGlPostingError(msg);
      setGlPostingStatus('failed');
    } finally {
      setGlPosting(false);
    }
  };

  const loadLines = useCallback(() => {}, []);

  useEffect(() => {
    uomAPI.getAll().then((r) => setUomOptions(r.data?.data || [])).catch(() => {});
    if (isAdd) {
      waybillAPI.nextSerial().then((r) => setForm((p) => ({ ...p, SerialNo: r.data.serial }))).catch(() => {});
    }
    if (!isAdd) {
      waybillAPI.getById(id).then((r) => {
        const wb = r.data;
        setForm({
          SerialNo: wb.SerialNo || '',
          TransactionDate: wb.TransactionDate ? wb.TransactionDate.split('T')[0] : today(),
          Shipper: wb.Shipper || '',
          ShipperAddress: wb.ShipperAddress || '',
          CustomerId: wb.CustomerId?._id || wb.CustomerId || '',
          customerName: wb.CustomerId?.CustomerName || wb.CustomerId?.name || '',
          CustomerAddress: wb.CustomerAddress || '',
          Origin: wb.Origin || '',
          Destination: wb.Destination || '',
          Vessel: wb.Vessel || '',
          Voyage: wb.Voyage || '',
          PlateNo: wb.PlateNo || '',
          VanNo: wb.VanNo || '',
          SealNo: wb.SealNo || '',
          VanSize: wb.VanSize || '',
          RateCode: wb.RateCode || '',
          HasWithHolding: !!wb.HasWithHolding,
          IsDelivered: !!wb.IsDelivered,
          Description: wb.Description || '',
          referenceNumber: wb.referenceNumber || '',
          notes: wb.notes || '',
          ShipmentType: wb.ShipmentType || 'freightforwarding',
          pullOutDate: wb.pullOutDate ? wb.pullOutDate.split('T')[0] : '',
        });
        setIsVoided(!!wb.isVoided);
        setVoidedInfo({ reason: wb.voidReason || '', at: wb.voidedAt || null });
        setMinAmount(wb.minimumBillingAmount ?? 250);
        setGlPostingStatus(wb.glPostingStatus || 'pending');
        setGlPostingError(wb.glPostingError || null);
        setLoading(false);
      }).catch(() => { setError('Failed to load waybill.'); setLoading(false); });
    }
    loadLines();
  }, [id, isAdd, loadLines]);

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [field]: val }));
  };

  // FF waybills: VanNo and Vessel (legacy Seal No.) move to WaybillLine — clear header on save.
  const isFF = form.ShipmentType === 'freightforwarding';

  const { mutate: handleSave, isPending: saving } = useGuardedMutation(async () => {
    setError('');
    setSuccess('');
    try {
      const payload = { ...form };
      if (isFF) {
        payload.VanNo  = '';
        payload.Vessel = '';
      }
      if (isAdd) {
        const r = await waybillAPI.create(payload);
        const newId = r.data?._id;
        navigate(`/billing/waybill/${newId}/edit`);
      } else {
        await waybillAPI.update(id, payload);
        setSuccess(`Waybill ${form.SerialNo} saved successfully.`);
        try {
          const r = await waybillAPI.getLinkedBillings(id);
          setLinkedBillings(Array.isArray(r.data?.data) ? r.data.data : []);
        } catch { /* non-fatal */ }
      }
    } catch (e) {
      const d = e?.response?.data;
      setError(d?.detail ? `${d.error}: ${d.detail}` : (d?.error || 'Failed to save waybill.'));
      throw e; // let useGuardedMutation know this attempt failed — keeps the idempotency key for retry
    }
  });

  const handlePrint = async () => {
    try {
      const res = await waybillAPI.getPrintToken(id);
      const data = res.data;
      if (data.success) {
        window.open(`/billing/reports/waybill/${data.printToken}`, '_blank');
      }
    } catch (e) {
      console.error('Failed to generate print token', e);
    }
  };

  const navigateToBilling = (billing) => {
    if (billing.billingType === 'FF') {
      navigate(`/billing/ff-billing/${billing._id}/edit`);
    } else {
      navigate(`/billing/trucking-billing/${billing._id}/edit`);
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) return;
    setVoiding(true);
    try {
      await waybillAPI.voidWaybill(id, voidReason);
      setIsVoided(true);
      setVoidedInfo({ reason: voidReason, at: new Date().toISOString() });
      setShowVoidModal(false);
      setVoidReason('');
      setSuccess(`Waybill ${form.SerialNo} has been voided.`);
      setAuditFetched(false);
      setAuditLog([]);
    } catch (e) {
      alert(e?.response?.data?.message || 'Failed to void waybill.');
    } finally {
      setVoiding(false);
    }
  };

  if (loading) {
    return (
      <Layout pageTitle="Waybill — Freight Forwarding">
        <div className="flex items-center justify-center h-48 text-gray-400">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle={isAdd ? 'New Waybill — Freight Forwarding' : `Waybill — Freight Forwarding: ${form.SerialNo}`}>
      <div className="space-y-6 max-w-8xl">
        {/* Tab bar */}
        {!isAdd && (
          <div className="border-b border-gray-200">
            <nav className="flex gap-0" aria-label="Tabs">
              {[
                { key: 'details', label: 'Details' },
                { key: 'audit',   label: 'Audit Trail' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* AUDIT TRAIL tab */}
        {!isAdd && activeTab === 'audit' && (
          <div className="space-y-6">
            {auditLoading && (
              <div className="text-center text-gray-400 py-12 text-sm">Loading audit trail…</div>
            )}
            {!auditLoading && auditLog.length === 0 && (
              <div className="text-center text-gray-400 py-12 text-sm">No audit records found.</div>
            )}
            {!auditLoading && auditLog.length > 0 && (
              <AuditTimeline entries={auditLog} />
            )}
          </div>
        )}

        {/* DETAILS tab (or add mode — always shown) */}
        {(isAdd || activeTab === 'details') && (
          <>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm flex items-center justify-between">
            <span>{success}</span>
            <button
              onClick={() => setSuccess('')}
              className="text-green-600 hover:text-green-800 ml-3 text-lg leading-none"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Voided banner */}
        {isVoided && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700 text-sm">
            <NoSymbolIcon className="h-4 w-4 flex-shrink-0" />
            <span>
              This waybill has been <b>VOIDED</b>
              {voidedInfo.reason && ` — Reason: ${voidedInfo.reason}`}
              {voidedInfo.at && ` on ${new Date(voidedInfo.at).toLocaleDateString('en-PH')}`}
            </span>
          </div>
        )}

        {/* Header Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Waybill Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Serial No." value={form.SerialNo} onChange={set('SerialNo')} />
            <Field label="Reference No." value={form.referenceNumber} onChange={set('referenceNumber')} />
            <Field label="Date" type="date" value={form.TransactionDate} onChange={set('TransactionDate')} />
            <Field label="Shipper" value={form.Shipper} onChange={set('Shipper')} />
            <Field label="Shipper Address" value={form.ShipperAddress} onChange={set('ShipperAddress')} />

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Consignee (Customer)</label>
              <CustomerSearch
                placeholder="Search consignee by name..."
                value={
                  form.CustomerId
                    ? {
                        _id:     form.CustomerId,
                        name:    form.customerName    || '',
                        address: form.CustomerAddress || '',
                      }
                    : null
                }
                onSelect={(customer) => {
                  if (!customer) {
                    setForm((p) => ({ ...p, CustomerId: '', customerName: '' }));
                    return;
                  }
                  const fullAddress = [customer.address, customer.city].filter(Boolean).join(', ');
                  setForm((p) => ({
                    ...p,
                    CustomerId:      customer._id,
                    customerName:    customer.name || '',
                    CustomerAddress: p.CustomerAddress?.trim() ? p.CustomerAddress : fullAddress,
                  }));
                }}
              />
            </div>

            <Field label="Consignee Address" value={form.CustomerAddress} onChange={set('CustomerAddress')} />
            <Field label="Origin" value={form.Origin} onChange={set('Origin')} />
            <Field label="Destination" value={form.Destination} onChange={set('Destination')} />
            {!isFF && (
              <Field label="Vessel / Truck / Van No. / Seal No." value={form.Vessel} onChange={set('Vessel')} />
            )}
            <Field label="Voyage" value={form.Voyage} onChange={set('Voyage')} />
            <Field label="Plate No." value={form.PlateNo} onChange={set('PlateNo')} />
            <Field label="Van Size" type="number" value={form.VanSize} onChange={set('VanSize')} />
            <Field label="Rate Code" type="number" value={form.RateCode} onChange={set('RateCode')} />
            {isFF && (
              <Field label="Pull-Out Date" type="date" value={form.pullOutDate} onChange={set('pullOutDate')} />
            )}

            <div className="flex items-center gap-6 col-span-full">
              <Checkbox label="Has Withholding" checked={form.HasWithHolding} onChange={set('HasWithHolding')} />
              <Checkbox label="Is Delivered" checked={form.IsDelivered} onChange={set('IsDelivered')} />
            </div>

            <div className="col-span-full">
              <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
              <textarea
                rows={2}
                value={form.Description}
                onChange={set('Description')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="col-span-full">
              <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={set('notes')}
                placeholder="Additional notes..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        {isAdd ? (
          <p className="text-sm text-gray-400 mt-2">Save the waybill header first to add line items.</p>
        ) : (
          <WaybillLineTable
            waybillId={id}
            hasWithholding={form.HasWithHolding}
            uomOptions={uomOptions}
            minAmount={minAmount}
            showVanSeal
            initialVanNo={form.VanNo}
            initialSealNo={form.Vessel}
            onGLUpdate={(gl) => {
              setGlPostingStatus(gl.glPostingStatus || 'pending');
              setGlPostingError(gl.glPostingError || null);
            }}
          />
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-3 flex-wrap">
          {isAdd ? (
            <>
              <ActionBtn onClick={handleSave} disabled={saving} label={saving ? 'Saving…' : 'Save'} primary />
              <ActionBtn onClick={() => navigate('/billing/waybill')} label="Cancel" />
            </>
          ) : (
            <>
              <ActionBtn onClick={handleSave} disabled={saving || isVoided} label={saving ? 'Saving…' : 'Save'} primary />
              <WaybillGLBadge
                status={glPostingStatus}
                error={glPostingError}
                waybillId={id}
                onRetry={handleRetryGL}
                loading={glPosting}
              />
              {!linkedBillingsLoaded ? (
                <ActionBtn label="Loading…" disabled />
              ) : linkedBillings.length === 0 ? (
                <ActionBtn
                  onClick={() => navigate(`/billing/ff-billing/add?waybillId=${id}`)}
                  label="Create Billing"
                  teal
                />
              ) : linkedBillings.length === 1 ? (
                <ActionBtn
                  onClick={() => navigateToBilling(linkedBillings[0])}
                  label={`View Billing #${linkedBillings[0].billingNo}`}
                  teal
                />
              ) : (
                <div className="relative" data-billings-menu>
                  <ActionBtn
                    onClick={() => setBillingsMenuOpen((v) => !v)}
                    label={`View Billings (${linkedBillings.length}) ▾`}
                    teal
                  />
                  {billingsMenuOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-[220px]">
                      {linkedBillings.map((b) => (
                        <button
                          key={b._id}
                          onClick={() => { setBillingsMenuOpen(false); navigateToBilling(b); }}
                          className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-800">
                            #{b.billingNo}
                            {b.isVoided && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">VOIDED</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {b.billingType} · {new Date(b.createdAt).toLocaleDateString('en-PH')}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <ActionBtn
                onClick={handlePrint}
                label="Print Waybill"
              />
              {!isVoided && (
                <button
                  onClick={() => setShowVoidModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-600 rounded-md text-sm hover:bg-red-50"
                >
                  <NoSymbolIcon className="h-4 w-4" />
                  Void Waybill
                </button>
              )}
              <ActionBtn onClick={() => navigate('/billing/waybill')} label="Back" />
            </>
          )}
        </div>

          </>
        )}

        {/* Void Modal */}
        {showVoidModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-md p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-2">Void Waybill</h3>
              <p className="text-sm text-gray-600 mb-3">
                This action cannot be undone. The waybill will be marked as void and no further edits will be allowed.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for voiding *</label>
              <textarea
                rows={3}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Enter reason..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => { setShowVoidModal(false); setVoidReason(''); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
                  disabled={voiding}
                >
                  Cancel
                </button>
                <button
                  onClick={handleVoid}
                  disabled={voiding || !voidReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {voiding ? 'Voiding…' : 'Confirm Void'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    </div>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 text-teal-600 rounded" />
      {label}
    </label>
  );
}

function ActionBtn({ label, onClick, disabled, primary, teal }) {
  const base = 'px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50';
  const cls = primary
    ? `${base} bg-teal-600 text-white hover:bg-teal-700`
    : teal
    ? `${base} bg-teal-50 text-teal-700 border border-teal-300 hover:bg-teal-100`
    : `${base} bg-gray-100 text-gray-700 hover:bg-gray-200`;
  return <button className={cls} onClick={onClick} disabled={disabled}>{label}</button>;
}
